import React, { useEffect, useMemo, useState } from 'react';
import { Bot, BrainCircuit, Cpu, Globe, HardDrive, HeartPulse, Layers3, RefreshCw, Send, ShieldCheck } from 'lucide-react';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import ModelSelector from '@/components/chat/ModelSelector';
import {
  mapleClawGateway,
  isMapleClawAvailable,
  type MapleClawSessionMeta,
  type MapleClawStatus,
  type MapleClawTranscriptEntry,
  type MapleClawProcessingRuntime,
} from '@/lib/mapleclawGateway';

const formatTime = (value?: string) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

const MapleClaw = () => {
  const { toast } = useToast();
  const models = useSupabaseStore((state) => state.models);
  const fetchModels = useSupabaseStore((state) => state.fetchModels);

  const [status, setStatus] = useState<MapleClawStatus | null>(null);
  const [sessions, setSessions] = useState<MapleClawSessionMeta[]>([]);
  const [selectedSessionKey, setSelectedSessionKey] = useState('main');
  const [transcript, setTranscript] = useState<MapleClawTranscriptEntry[]>([]);
  const [message, setMessage] = useState('');
  const [workspacePath, setWorkspacePath] = useState('MapleClaw');
  const [heartbeatMinutes, setHeartbeatMinutes] = useState('30');
  const [autoHeartbeat, setAutoHeartbeat] = useState(true);
  const [processingRuntime, setProcessingRuntime] = useState<MapleClawProcessingRuntime>('local');
  const [piAgentModelId, setPiAgentModelId] = useState('');
  const [piAgentInstructions, setPiAgentInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const available = isMapleClawAvailable();

  const refresh = async (sessionKey = selectedSessionKey) => {
    if (!available) return;
    const [nextStatus, nextConfig, nextSessions, nextTranscript] = await Promise.all([
      mapleClawGateway.getStatus(),
      mapleClawGateway.getConfig(),
      mapleClawGateway.listSessions(),
      mapleClawGateway.getTranscript(sessionKey, 60),
    ]);

    setStatus(nextStatus);
    setSessions(nextSessions);
    setTranscript(nextTranscript);
    setWorkspacePath(nextStatus.workspacePath);
    setHeartbeatMinutes(String(Math.max(1, Math.round(nextStatus.heartbeatMs / 60000))));
    setAutoHeartbeat(nextStatus.autoHeartbeat);
    setProcessingRuntime(nextConfig.processingRuntime);
    setPiAgentModelId(nextConfig.piAgentModelId || '');
    setPiAgentInstructions(nextConfig.piAgentInstructions || '');
  };

  useEffect(() => {
    if (!models?.length) {
      fetchModels().catch(() => undefined);
    }
  }, [models, fetchModels]);

  useEffect(() => {
    if (!available) return;

    void refresh('main');
    const unsubscribe = mapleClawGateway.subscribe(() => {
      void refresh(selectedSessionKey);
    });

    return unsubscribe;
  }, [available, selectedSessionKey]);

  const selectedSession = useMemo(
    () => sessions.find((session) => session.key === selectedSessionKey) || null,
    [sessions, selectedSessionKey]
  );

  const runtimeLabel = status?.runtime === 'electron' ? 'Electron' : status?.runtime === 'browser' ? 'Browser' : 'Unavailable';
  const runtimeIcon = status?.runtime === 'electron' ? HardDrive : Globe;
  const RuntimeIcon = runtimeIcon;

  const handleSaveConfig = async () => {
    if (!available) return;
    setLoading(true);
    try {
      await mapleClawGateway.setConfig({
        workspacePath,
        autoHeartbeat,
        defaultSessionKey: selectedSessionKey || 'main',
        heartbeatMs: Math.max(1, Number(heartbeatMinutes || '30')) * 60 * 1000,
        processingRuntime,
        piAgentModelId,
        piAgentInstructions,
      });
      await refresh(selectedSessionKey);
      toast({ title: 'MapleClaw updated', description: 'Gateway settings were saved.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!available || !message.trim()) return;
    setLoading(true);
    try {
      const result = await mapleClawGateway.enqueue({
        sessionKey: selectedSessionKey,
        type: 'user_message',
        payload: message,
        awaitResponse: true,
        processingRuntime,
        modelId: processingRuntime === 'pi-agent' ? piAgentModelId || undefined : undefined,
        agentInstruction: processingRuntime === 'pi-agent' ? piAgentInstructions : undefined,
      });

      if (!result.ok) {
        toast({ title: 'MapleClaw failed', description: result.error || 'The request could not be processed.', variant: 'destructive' });
        return;
      }

      setMessage('');
      await refresh(selectedSessionKey);
    } finally {
      setLoading(false);
    }
  };

  const handleHeartbeat = async () => {
    if (!available) return;
    setLoading(true);
    try {
      await mapleClawGateway.enqueue({
        sessionKey: selectedSessionKey,
        type: 'heartbeat',
        payload: 'Manual MapleClaw heartbeat',
        awaitResponse: true,
      });
      await refresh(selectedSessionKey);
    } finally {
      setLoading(false);
    }
  };

  if (!available) {
    return (
      <div className="flex h-full items-center justify-center bg-background p-6">
        <Card className="max-w-2xl border-border/60 bg-card/90">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <ShieldCheck className="h-6 w-6 text-primary" />
              MapleClaw Gateway
            </CardTitle>
            <CardDescription>
              MapleClaw needs either the Electron desktop shell or browser storage support in the current environment.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid h-full grid-cols-1 gap-4 overflow-hidden bg-background p-4 xl:grid-cols-[320px_minmax(0,1fr)]">
      <div className="space-y-4 overflow-auto pr-1">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Bot className="h-5 w-5 text-primary" />
              MapleClaw Gateway
            </CardTitle>
            <CardDescription>
              Local control plane for MapleOS sessions, heartbeat events, and Blueprint execution.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              <Badge variant="secondary">{status?.running ? 'Running' : 'Idle'}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Runtime host</span>
              <Badge variant="outline" className="gap-1">
                <RuntimeIcon className="h-3.5 w-3.5" />
                {runtimeLabel}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Message runtime</span>
              <Badge variant="outline" className="gap-1">
                <BrainCircuit className="h-3.5 w-3.5" />
                {processingRuntime === 'pi-agent' ? 'pi-agent' : 'local'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Sessions</span>
              <span>{status?.sessionCount ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Active queues</span>
              <span>{status?.activeSessions ?? 0}</span>
            </div>
            <div className="rounded-md border border-border/60 bg-muted/40 p-3 text-xs text-muted-foreground">
              Workspace: <span className="font-mono text-foreground">{status?.workspacePath || 'MapleClaw'}</span>
            </div>
            <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-xs text-muted-foreground">
              {status?.runtime === 'electron'
                ? 'Electron mode stores MapleClaw sessions and transcripts on the local filesystem.'
                : 'Browser mode stores MapleClaw sessions and transcripts in local browser storage.'}
            </div>
            <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground">
              {processingRuntime === 'pi-agent'
                ? 'pi-agent mode routes MapleClaw turns through the MaplePT-backed server path with operator/tool-aware instructions.'
                : 'Local mode uses MapleClaw’s direct model path for quick local orchestration replies.'}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 gap-2" onClick={() => void refresh(selectedSessionKey)} disabled={loading}>
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
              <Button className="flex-1 gap-2" onClick={handleHeartbeat} disabled={loading}>
                <HeartPulse className="h-4 w-4" />
                Ping
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Cpu className="h-4 w-4 text-primary" />
              Gateway settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mapleclaw-workspace">Workspace folder</Label>
              <Input id="mapleclaw-workspace" value={workspacePath} onChange={(event) => setWorkspacePath(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mapleclaw-session">Default session</Label>
              <Input id="mapleclaw-session" value={selectedSessionKey} onChange={(event) => setSelectedSessionKey(event.target.value || 'main')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mapleclaw-heartbeat">Heartbeat interval (minutes)</Label>
              <Input id="mapleclaw-heartbeat" type="number" min="1" value={heartbeatMinutes} onChange={(event) => setHeartbeatMinutes(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Message runtime</Label>
              <Select value={processingRuntime} onValueChange={(value) => setProcessingRuntime(value as MapleClawProcessingRuntime)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select runtime" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="local">Local MapleClaw</SelectItem>
                  <SelectItem value="pi-agent">pi-agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {processingRuntime === 'pi-agent' ? (
              <>
                <div className="space-y-2">
                  <Label>pi-agent model</Label>
                  <ModelSelector models={models || []} selectedModelId={piAgentModelId} onModelChange={setPiAgentModelId} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mapleclaw-pi-instructions">pi-agent instructions</Label>
                  <Textarea
                    id="mapleclaw-pi-instructions"
                    rows={4}
                    value={piAgentInstructions}
                    onChange={(event) => setPiAgentInstructions(event.target.value)}
                    placeholder="Optional system guidance for pi-agent responses"
                  />
                </div>
              </>
            ) : null}
            <div className="flex items-center justify-between rounded-md border border-border/60 p-3">
              <div>
                <div className="font-medium">Automatic heartbeats</div>
                <div className="text-xs text-muted-foreground">Keeps the default session warm in the local queue.</div>
              </div>
              <Switch checked={autoHeartbeat} onCheckedChange={setAutoHeartbeat} />
            </div>
            <Button className="w-full" onClick={handleSaveConfig} disabled={loading}>
              Save settings
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Layers3 className="h-4 w-4 text-primary" />
              Sessions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {sessions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No sessions yet. Send a message or ping a heartbeat to create one.</p>
            ) : (
              sessions.map((session) => (
                <button
                  key={session.id}
                  type="button"
                  className={`w-full rounded-md border p-3 text-left transition ${session.key === selectedSessionKey ? 'border-primary bg-primary/5' : 'border-border/60 hover:border-primary/40'}`}
                  onClick={() => {
                    setSelectedSessionKey(session.key);
                    void refresh(session.key);
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{session.key}</span>
                    <Badge variant={session.processing ? 'default' : 'secondary'}>{session.queueSize > 0 ? `${session.queueSize} queued` : 'ready'}</Badge>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">Last active {formatTime(session.lastActivityAt)}</div>
                </button>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid min-h-0 grid-rows-[auto_auto_1fr] gap-4 overflow-hidden">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Session console</CardTitle>
            <CardDescription>
              Queue a message into MapleClaw or inspect the latest transcript entries for the current session.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Ask MapleClaw to inspect a workflow, summarize a session, or prepare a local action plan."
              rows={4}
            />
            <div className="flex flex-wrap gap-2">
              <Button className="gap-2" onClick={handleSend} disabled={loading || !message.trim()}>
                <Send className="h-4 w-4" />
                Send to MapleClaw
              </Button>
              <Button variant="outline" onClick={() => setMessage(`Review session \"${selectedSessionKey}\" and summarize the pending work.`)}>
                Insert summary prompt
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-wrap items-center gap-4 py-4 text-sm">
            <div>
              <span className="text-muted-foreground">Current session:</span>{' '}
              <span className="font-medium">{selectedSessionKey}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Transcript file:</span>{' '}
              <span className="font-mono text-xs">{selectedSession?.transcriptPath || 'Not created yet'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Last reply:</span>{' '}
              <span>{selectedSession?.lastAssistantReply || '—'}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="min-h-0 overflow-hidden">
          <CardHeader>
            <CardTitle className="text-base">Transcript tail</CardTitle>
          </CardHeader>
          <CardContent className="min-h-0">
            <ScrollArea className="h-[calc(100vh-24rem)] pr-4">
              <div className="space-y-4">
                {transcript.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No transcript events yet for this session.</p>
                ) : (
                  transcript.map((entry) => (
                    <div key={entry.id} className="rounded-lg border border-border/60 bg-muted/30 p-4">
                      <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                        <Badge variant="outline">{entry.role}</Badge>
                        <span>{entry.eventType}</span>
                        <span>{formatTime(entry.timestamp)}</span>
                      </div>
                      <Separator className="my-3" />
                      <p className="whitespace-pre-wrap text-sm leading-6">{entry.content}</p>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MapleClaw;
